import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";

export function TermsPage() {
  return (
    <div>
      <SEO
        title="Terms of Service"
        description="Read the terms and conditions for using GetSent's DMARC monitoring and email authentication services."
        canonical="/terms"
      />
      {/* Header */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-slate-50/50 to-transparent py-16 dark:from-slate-950/20 sm:py-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Terms of Service</h1>
          <p className="mt-4 text-muted-foreground">Last updated: January 1, 2025</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="lead text-lg text-muted-foreground">
              Please read these Terms of Service ("Terms", "Terms of Service") carefully before using the GetSent
              service operated by GetSent, Inc. ("us", "we", or "our").
            </p>

            <h2 className="mt-10 text-xl font-semibold">1. Acceptance of Terms</h2>
            <p className="mt-4 text-muted-foreground">
              By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any
              part of the terms, then you may not access the Service.
            </p>

            <h2 className="mt-10 text-xl font-semibold">2. Description of Service</h2>
            <p className="mt-4 text-muted-foreground">
              GetSent provides a DMARC monitoring and email authentication analytics platform. The Service includes:
            </p>
            <ul className="mt-4 list-disc pl-6 text-muted-foreground space-y-2">
              <li>DMARC report aggregation and analysis</li>
              <li>Email authentication monitoring (SPF, DKIM, DMARC)</li>
              <li>DNS record validation tools</li>
              <li>Dashboards and reporting features</li>
            </ul>

            <h2 className="mt-10 text-xl font-semibold">3. Account Registration</h2>
            <p className="mt-4 text-muted-foreground">
              To use certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul className="mt-4 list-disc pl-6 text-muted-foreground space-y-2">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password and accept all risks of unauthorized access</li>
              <li>Immediately notify us if you discover any unauthorized use of your account</li>
            </ul>

            <h2 className="mt-10 text-xl font-semibold">4. Subscription and Billing</h2>
            <p className="mt-4 text-muted-foreground">
              Some parts of the Service are billed on a subscription basis. You will be billed in advance on a
              recurring basis ("Billing Cycle"). Billing cycles are set on a monthly or annual basis.
            </p>
            <p className="mt-4 text-muted-foreground">
              A valid payment method is required to process the payment for your Subscription. You shall provide
              accurate and complete billing information.
            </p>

            <h2 className="mt-10 text-xl font-semibold">5. Free Trial</h2>
            <p className="mt-4 text-muted-foreground">
              We may offer a free trial to new users. The trial is subject to the limitations of the free tier
              and may be cancelled or modified at any time.
            </p>

            <h2 className="mt-10 text-xl font-semibold">6. Acceptable Use</h2>
            <p className="mt-4 text-muted-foreground">
              You agree not to use the Service:
            </p>
            <ul className="mt-4 list-disc pl-6 text-muted-foreground space-y-2">
              <li>For any unlawful purpose or to violate any laws</li>
              <li>To send spam or unsolicited communications</li>
              <li>To interfere with or disrupt the Service or servers</li>
              <li>To attempt to gain unauthorized access to any portion of the Service</li>
              <li>To harass, abuse, or harm another person or entity</li>
            </ul>

            <h2 className="mt-10 text-xl font-semibold">7. Intellectual Property</h2>
            <p className="mt-4 text-muted-foreground">
              The Service and its original content, features, and functionality are and will remain the exclusive
              property of GetSent, Inc. and its licensors. The Service is protected by copyright, trademark, and
              other laws.
            </p>

            <h2 className="mt-10 text-xl font-semibold">8. Data Ownership</h2>
            <p className="mt-4 text-muted-foreground">
              You retain all rights to your data. By using the Service, you grant us a limited license to process
              your DMARC reports and related data solely to provide the Service to you.
            </p>

            <h2 className="mt-10 text-xl font-semibold">9. Limitation of Liability</h2>
            <p className="mt-4 text-muted-foreground">
              In no event shall GetSent, Inc., nor its directors, employees, partners, agents, suppliers, or
              affiliates, be liable for any indirect, incidental, special, consequential or punitive damages,
              including without limitation, loss of profits, data, use, goodwill, or other intangible losses.
            </p>

            <h2 className="mt-10 text-xl font-semibold">10. Disclaimer</h2>
            <p className="mt-4 text-muted-foreground">
              Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE"
              basis. The Service is provided without warranties of any kind, whether express or implied.
            </p>

            <h2 className="mt-10 text-xl font-semibold">11. Termination</h2>
            <p className="mt-4 text-muted-foreground">
              We may terminate or suspend your account immediately, without prior notice or liability, for any
              reason whatsoever, including without limitation if you breach the Terms.
            </p>

            <h2 className="mt-10 text-xl font-semibold">12. Changes to Terms</h2>
            <p className="mt-4 text-muted-foreground">
              We reserve the right to modify or replace these Terms at any time. If a revision is material, we
              will try to provide at least 30 days' notice prior to any new terms taking effect.
            </p>

            <h2 className="mt-10 text-xl font-semibold">13. Contact Us</h2>
            <p className="mt-4 text-muted-foreground">
              If you have any questions about these Terms, please contact us at{" "}
              <a href="mailto:legal@getsent.dev" className="text-indigo-600 hover:text-indigo-500">
                legal@getsent.dev
              </a>
              .
            </p>
          </div>

          <div className="mt-12 flex items-center gap-4 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground">
              Privacy Policy
            </Link>
            <span>&middot;</span>
            <Link to="/" className="hover:text-foreground">
              Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
